import Button, { IconButton } from '@atlaskit/button/new';
import Heading from '@atlaskit/heading';
import DeleteIcon from '@atlaskit/icon/core/delete';
import { Inline, Stack, xcss } from '@atlaskit/primitives';
import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import FieldPicker from '@databases/field-picker/FieldPicker';
import { defaultStoreUtils } from '@databases/redux';
import { DatabaseFieldType } from '@databases/types/database';
import {
  type DatabaseFilter,
  DatabaseFilterType,
  isFilterWithUsageType,
} from '@databases/types/database/view';
import {
  getInitialValue,
  getSupportedFilterTypes,
} from '@databases/utils/database/field-types';
import {
  getDatabaseFieldOptional,
  getDatabaseFields,
} from '@databases/utils/database/utils';
import {
  type ViewChangeType,
  ViewFilterChangeType,
} from '@databases/utils/database/view/view';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import { FilterTypePicker } from '@databases/view-settings/FilterTypePicker';
import FilterValueEdit from '@databases/view-settings/FilterValueEdit';
import { cloneDeep, isEqual } from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl-next';
import { v4 as uuidv4 } from 'uuid';

import { useFilterSettings } from '../../hooks/useFilterSettings';
import { messages } from '../../i18n';

const editContainerStyles = xcss({
  borderColor: 'color.border',
  borderWidth: 'border.width',
  borderStyle: 'solid',
  borderRadius: 'border.radius.100',
  padding: 'space.250',
});

export interface ViewSettingsFilterRuleEditProps {
  contentId: string;
  filter?: DatabaseFilter;
  onCancel: () => void;
  onDelete?: () => void;
  onSubmit: (filter: DatabaseFilter) => void;
}

export const ViewSettingsFilterRuleEdit = ({
  contentId,
  filter,
  onCancel,
  onDelete,
  onSubmit,
}: ViewSettingsFilterRuleEditProps) => {
  const DEFAULT_NEW_FILTER: DatabaseFilter = useMemo(() => {
    return {
      id: uuidv4(),
      type: DatabaseFilterType.EQUALS,
      fieldId: '',
      value: getInitialValue(DatabaseFieldType.TEXT),
    };
  }, []);

  const client = useAnalytics();
  const { formatMessage } = useIntl();

  const [draftFilter, setDraftFilter] = useState<DatabaseFilter>(() =>
    cloneDeep(filter || DEFAULT_NEW_FILTER)
  );

  const hasChanged = useMemo(() => {
    return !isEqual(draftFilter, filter);
  }, [draftFilter, filter]);

  const store = defaultStoreUtils.useStore();

  const handleChange = useCallback(
    (delta: ViewChangeType) => {
      if (
        delta.type === ViewFilterChangeType.VIEW_FILTERS_SET_FIELD_ID ||
        delta.type === ViewFilterChangeType.VIEW_FILTERS_SET_FILTER_TYPE
      ) {
        /**
         * `useFilterSettings` does not allow `delta.filter` in these change events to be
         * `undefined`, but we explicitly handle that case here in order to be exhaustive
         * for TypeScript and also to guard against future regressions
         */
        setDraftFilter(cloneDeep(delta.filter || DEFAULT_NEW_FILTER));
      }

      if (delta.type === ViewFilterChangeType.VIEW_FILTERS_SET_VALUE) {
        setDraftFilter((currentDraftFilter: DatabaseFilter) => {
          /**
           * The following branches that return early should not be reachable, but we explicitly
           * handle these cases here in order to be exhaustive for TypeScript and also to guard
           * against future regressions
           */

          // This component isn't meant for editing of AND or OR filter types
          if (isFilterWithUsageType(currentDraftFilter)) {
            return currentDraftFilter;
          }
          // `useFilterSettings` doesn't fire value change events if the filter type is EMPTY
          if (
            currentDraftFilter.type === DatabaseFilterType.EMPTY ||
            delta.filter.type === DatabaseFilterType.EMPTY
          ) {
            return currentDraftFilter;
          }
          if (currentDraftFilter.type === DatabaseFilterType.CONTAINS) {
            // if the filter type is CONTAINS, `useFilterSettings` + `FilterValueEdit` only
            // fire value change events with string values
            return typeof delta.filter.value === 'string'
              ? { ...currentDraftFilter, value: delta.filter.value }
              : currentDraftFilter;
          }
          // `useFilterSettings` + `FilterValueEdit`` ensure the values in value change events
          // for the remaining filter types are `DatabaseValue`
          if (
            typeof delta.filter.value === 'string' ||
            Array.isArray(delta.filter.value)
          ) {
            return currentDraftFilter;
          }

          return { ...currentDraftFilter, value: delta.filter.value };
        });
      }
    },
    [DEFAULT_NEW_FILTER]
  );

  const { handleFieldChange, handleFilterTypeChange, handleFilterValueChange } =
    useFilterSettings({
      onChange: handleChange,
      filterObject: draftFilter,
      contentId,
      store,
    });

  const [fields, field] = defaultStoreUtils.useEqualSelector((state) => {
    const database = selectDatabaseByContentIdRequired(state, {
      contentId,
    });
    const fieldId =
      !draftFilter || isFilterWithUsageType(draftFilter)
        ? undefined
        : draftFilter.fieldId;
    return [getDatabaseFields(database), getDatabaseFieldOptional(database, fieldId)];
  });

  const filterTypes = useMemo(
    () => (field ? getSupportedFilterTypes(field.type) : undefined),
    [field]
  );

  const fieldType = field?.type;

  const handleSubmit = useCallback(() => {
    if (fieldType) {
      client.sendDatabaseFilteredEvent({
        contentId,
        databaseContainer: getDatabaseContainerType(),
        fieldType,
        source: 'databaseViewSettingsPanel',
      });
    }
    onSubmit(draftFilter);
  }, [client, contentId, draftFilter, fieldType, onSubmit]);

  return (
    <Stack space="space.200" xcss={editContainerStyles}>
      <Inline alignBlock="center" grow="fill" spread="space-between">
        <Heading size="xxsmall" as="h3">
          {filter
            ? formatMessage(
                messages['view-settings-panel-filter.edit-filter-update-heading']
              )
            : formatMessage(
                messages['view-settings-panel-filter.edit-filter-create-heading']
              )}
        </Heading>
        {onDelete && (
          <IconButton
            appearance="subtle"
            icon={DeleteIcon}
            label={formatMessage(
              messages['view-settings-panel-filter.filter-delete-action-label']
            )}
            onClick={onDelete}
            spacing="compact"
          />
        )}
      </Inline>
      <Stack space="space.100">
        <FieldPicker
          fields={fields}
          value={field}
          onChange={handleFieldChange}
          autoFocus={!field}
          placeholder={formatMessage(
            messages['view-settings-panel-filter.select-field-placeholder']
          )}
        />
        <FilterTypePicker
          contentId={contentId}
          value={draftFilter}
          onChange={handleFilterTypeChange}
          types={filterTypes}
        />
        <FilterValueEdit
          contentId={contentId}
          value={draftFilter}
          onChange={handleFilterValueChange}
          autoFocus={!!field}
          appearance="default"
        />
      </Stack>
      <Inline space="space.050">
        <Button
          appearance="primary"
          isDisabled={!hasChanged}
          onClick={handleSubmit}
          spacing="compact"
        >
          {filter
            ? formatMessage(
                messages['view-settings-panel-filter.edit-filter-update-button']
              )
            : formatMessage(
                messages['view-settings-panel-filter.edit-filter-create-button']
              )}
        </Button>
        <Button appearance="subtle" onClick={onCancel} spacing="compact">
          {formatMessage(
            messages['view-settings-panel-filter.edit-filter-cancel-button']
          )}
        </Button>
      </Inline>
    </Stack>
  );
};
